<template>
    <div data-spec-class="ip_configuration_fieldset">
        <ColumnTitle class="p-3">
            {{ $t('configuratorGeneral.ipConfiguration') }}
        </ColumnTitle>

        <Dropdown
            v-model="ipv4Subnet"
            :class="[
                'mb-3',
                changeAnimationClass,
                isIpSubnetOverridden ? 'bg-gray-600' : 'bg-gray-700',
            ]"
            name="ipv4Subnet"
            :label="$t('configuratorGeneral.ipv4Subnet')"
            :options="ipv4SubnetList"
            :show-empty-value="true"
            data-spec-class="ip_configuration_fieldset--ipv4Subnet"
        />
        <Dropdown
            v-model="ipv6Subnet"
            :class="[changeAnimationClass, isIpSubnetOverridden ? 'bg-gray-600' : 'bg-gray-700']"
            name="ipv6Subnet"
            :label="$t('configuratorGeneral.ipv6Subnet')"
            :options="ipv6SubnetList"
            :show-empty-value="true"
            data-spec-class="ip_configuration_fieldset--ipv6Subnet"
        />
        <template v-if="canSelectIpAddresses">
            <BaseDivider class="mt-4 mb-3"> {{ $t('configuratorGeneral.or') }} </BaseDivider>
            {{ fields.ipv4Address }}
            <Range
                v-model="ipv4Addresses"
                :class="[
                    changeAnimationClass,
                    isIpAddressesOverridden ? 'bg-gray-600' : 'bg-gray-700',
                ]"
                name="ipv4Addresses"
                :min="assortment.ipv4Address.min"
                :max="assortment.ipv4Address.max"
                :label="$t('configuratorGeneral.ipv4Addresses')"
                :badge="`${$t('configuratorGeneral.ipv4AddressesFree', {
                    count: props.assortment.ipv4Address.free,
                })}`"
                data-spec-class="ip_configuration_fieldset--ipv4Address"
            />
        </template>
    </div>
</template>

<script setup>
import { computed, defineProps, watch, ref } from 'vue';
import { mapSubnetAttribute } from '../assortments/AssortmentManager';
import ColumnTitle from './Configurator/ColumnTitle.vue';
import Dropdown from './Configurator/Dropdown.vue';
import Range from './Configurator/Range.vue';
import BaseDivider from '../../../components/BaseDivider.vue';
import { delay } from '../../../utils/timeUtils';

const animationDuration = 300;

const props = defineProps({
    assortment: { type: Object, required: true },
    fields: { type: Object, required: true },
    selection: { type: Object, required: true },
    canSelectIpAddresses: { type: Boolean, default: true },
});

const emit = defineEmits(['update:fields']);

const updateFields = (patch) => {
    emit('update:fields', patch);
};

const ipv4Subnet = computed({
    get: () => props.fields.ipv4Subnet,
    set: (value) => updateFields({ ipv4Subnet: value }),
});

const ipv6Subnet = computed({
    get: () => props.fields.ipv6Subnet,
    set: (value) => updateFields({ ipv6Subnet: value }),
});

const ipv4Addresses = computed({
    get: () => props.fields.ipv4Addresses,
    set: (value) => updateFields({ ipv4Addresses: value }),
});

const isIpSubnetOverridden = ref(false);
const isIpAddressesOverridden = ref(false);

const ipv4SubnetList = computed(() => {
    return mapSubnetAttribute(props.assortment.ipv4Subnets, 'name', ' Subnet');
});

const ipv6SubnetList = computed(() => {
    return mapSubnetAttribute(props.assortment.ipv6Subnets, 'name', ' Subnet');
});

const changeAnimationClass = '-m-1.5 rounded-sm p-1.5 transition duration-300 ease-out';

watch(
    () => props.selection.ipv4Subnet,
    (value) => {
        if (value && props.selection.ipv4Addresses > 0) {
            updateFields({ ipv4Addresses: 0 });
            isIpAddressesOverridden.value = true;
            delay(animationDuration).then(() => {
                isIpAddressesOverridden.value = false;
            });
        }
    }
);

watch(
    () => props.selection.ipv6Subnet,
    (value) => {
        if (value && props.selection.ipv4Addresses > 0) {
            updateFields({ ipv4Addresses: 0 });
            isIpAddressesOverridden.value = true;
            delay(animationDuration).then(() => {
                isIpAddressesOverridden.value = false;
            });
        }
    }
);

watch(
    () => props.selection.ipv4Addresses,
    (value) => {
        if (value && (props.selection.ipv4Subnet || props.selection.ipv6Subnet)) {
            updateFields({ ipv4Subnet: null, ipv6Subnet: null });
            isIpSubnetOverridden.value = true;
            delay(animationDuration).then(() => {
                isIpSubnetOverridden.value = false;
            });
        }
    }
);
</script>
